import { useCallback } from 'react';

import {
  Button,
  MenuButton,
  MenuItem,
  MenuItemGroup,
  Ryecon,
  RyeconEllipsisHorizontal,
  RyeconLinkExternal,
  RyeconShare,
  Sizes,
  toast,
} from '@dbt-labs/sourdough';

import { ButtonAction } from '@/pages/account/project/resource/details/components/HeaderButtons';

export type OpenInMenuItem = {
  label: string;
  value: ButtonAction;
  ryecon?: Ryecon;
  description?: string;
  includeTopDivider?: boolean;
  show?: boolean;
};

export type OpenInMenuGroup = {
  label: string;
  ryecon?: Ryecon;
  items: OpenInMenuItem[];
  includeTopDivider?: boolean;
  show?: boolean;
};

type OpenInMenuButtonProps = {
  items: (OpenInMenuItem | OpenInMenuGroup)[];
  onClick: (value: ButtonAction) => void;
};

export const OpenInMenuButton = ({ items, onClick }: OpenInMenuButtonProps) => {
  const handleClick = useCallback(
    (value: ButtonAction) => {
      onClick(value);
    },
    [onClick],
  );

  const filteredItems = items.filter((item) => !('show' in item) || item.show);

  if (filteredItems.length === 0) return null;

  const firstItem = filteredItems[0];
  if (filteredItems.length > 0 && 'includeTopDivider' in firstItem) {
    filteredItems[0].includeTopDivider = false;
  }

  const hasOneItem = filteredItems.length === 1;
  const isFirstItemGroup = 'items' in firstItem;
  const hasOneItemInFirstGroup = isFirstItemGroup && firstItem.items.length === 1;

  if (hasOneItem && (!isFirstItemGroup || hasOneItemInFirstGroup)) {
    const item = isFirstItemGroup ? firstItem.items[0] : firstItem;
    return (
      <Button
        testId="open-in-button"
        onClick={() => handleClick(item.value)}
        ryecon={firstItem.ryecon}
      >
        Open in {firstItem.label}
      </Button>
    );
  }

  return (
    <MenuButton
      testId="open-in-menu-button"
      label={hasOneItem ? `Open in ${firstItem.label}` : 'Open in...'}
      text={hasOneItem ? `Open in ${firstItem.label}` : 'Open in...'}
      buttonStyle="primary"
      ryecon={RyeconLinkExternal}
      items={filteredItems as (MenuItem | MenuItemGroup)[]}
      onClick={handleClick}
      menuPlacement="bottom"
      menuAlignment="right"
    />
  );
};

export type ActionButtonsProps = {
  fetchingItems?: boolean;
  openInItems: (OpenInMenuItem | OpenInMenuGroup)[];
  moreItems?: MenuItem[];
  onClick: (value: ButtonAction) => void;
};

export const ActionButtons = ({
  fetchingItems = false,
  openInItems,
  moreItems = [
    {
      label: 'Share',
      value: 'share',
      ryecon: RyeconShare,
    },
  ],
  onClick,
}: ActionButtonsProps) => {
  if (fetchingItems) {
    return null;
  }

  const filteredOpenInItems = openInItems.filter(
    (item) => !('show' in item) || item.show,
  );

  // if there are no open in items, just show the share button
  if (filteredOpenInItems.length === 0) {
    return (
      <Button
        text="Share"
        tooltip="Copy link to page"
        ryecon={RyeconShare}
        onClick={() => {
          navigator.clipboard.writeText(window.location.href);
          toast.success('Copied link to clipboard', { id: 'copy-link' });
        }}
        type="secondary"
        size={Sizes.md}
      />
    );
  }

  return (
    <>
      <OpenInMenuButton items={filteredOpenInItems} onClick={onClick} />
      <MenuButton
        testId="more-items-menu-button"
        items={moreItems}
        onClick={onClick}
        buttonVariant={{ variant: 'ryecon', ryecon: RyeconEllipsisHorizontal }}
        menuPlacement="bottom"
        menuAlignment="right"
      />
    </>
  );
};
